import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminAuth } from '@/context/AdminAuthContext';

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { auth, openModal } = useAdminAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleLang = () => {
    const next = i18n.language.startsWith('en') ? 'es' : 'en';
    i18n.changeLanguage(next);
  };

  const currentLang = i18n.language.startsWith('en') ? 'EN' : 'ES';
  const targetLang = i18n.language.startsWith('en') ? 'ES' : 'EN';

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/about', label: t('nav_about') },
    { to: '/services', label: t('nav_services') },
    { to: '/contact', label: t('nav_contact') },
  ];

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-black/90 backdrop-blur-sm border-b border-white/5`}>
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              alt="FMO LLC Insulation Services"
              className="h-14 w-auto object-contain"
              src="https://static.readdy.ai/image/3ce9d24c92e6c33dbaca65a0380531ab/059954b6faaad54bb7acfcf00b0835ac.png"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-base font-bold tracking-wider whitespace-nowrap transition-colors duration-200 ${
                  location.pathname === link.to ? 'text-primary-500' : 'text-foreground-50 hover:text-primary-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Language toggle - single click */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-2 whitespace-nowrap bg-white/5 border border-white/10 hover:border-primary-500 hover:bg-primary-500/10 text-foreground-50 text-sm font-bold px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer"
              title={i18n.language.startsWith('en') ? 'Switch to Spanish' : 'Cambiar a Inglés'}
            >
              <i className="ri-global-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span>{targetLang}</span>
            </button>

            <a
              href="tel:+15747219810"
              className="hidden md:flex items-center gap-2 whitespace-nowrap border border-primary-500/40 hover:border-primary-500 text-primary-500 text-base font-bold px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center"></i>
              (574) 721-9810
            </a>

            <Link
              to="/contact"
              className="hidden md:flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-base font-bold px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-mail-send-line text-lg w-5 h-5 flex items-center justify-center"></i>
              {t('nav_get_quote')}
            </Link>

            {/* Admin icon */}
            <button
              onClick={auth.isLoggedIn ? undefined : openModal}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-primary-500 hover:bg-primary-500/10 text-foreground-400 hover:text-primary-500 transition-all duration-200 cursor-pointer"
              title={auth.isLoggedIn ? 'Admin Panel' : 'Acceso Administrativo'}
            >
              {auth.isLoggedIn ? (
                <Link to="/admin" className="w-full h-full flex items-center justify-center">
                  <i className="ri-user-star-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </Link>
              ) : (
                <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
              )}
            </button>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-50"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`text-2xl w-6 h-6 flex items-center justify-center ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-black/95 border-t border-white/10 px-4 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-base font-bold tracking-wider whitespace-nowrap transition-colors duration-200 ${
                location.pathname === link.to ? 'text-primary-500' : 'text-foreground-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={toggleLang}
            className="flex items-center gap-2 whitespace-nowrap bg-white/5 border border-white/10 hover:border-primary-500 text-foreground-50 text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200 cursor-pointer w-fit"
          >
            <i className="ri-global-line text-lg w-5 h-5 flex items-center justify-center"></i>
            <span>{targetLang}</span>
          </button>
          <a
            href="tel:+15747219810"
            className="flex items-center gap-2 whitespace-nowrap border border-primary-500/40 text-primary-500 text-base font-bold px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer w-fit"
          >
            <i className="ri-phone-line text-lg w-6 h-6 flex items-center justify-center"></i>
            (574) 721-9810
          </a>
          <Link
            to="/contact"
            className="flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-base font-bold px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer w-fit"
          >
            <i className="ri-mail-send-line text-lg w-6 h-6 flex items-center justify-center"></i>
            {t('nav_get_quote')}
          </Link>
          {/* Admin button in mobile menu */}
          {auth.isLoggedIn ? (
            <Link
              to="/admin"
              className="flex items-center gap-2 whitespace-nowrap bg-white/5 border border-white/10 text-foreground-400 text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200 cursor-pointer w-fit"
            >
              <i className="ri-user-star-line w-5 h-5 flex items-center justify-center"></i>
              Panel Admin
            </Link>
          ) : (
            <button
              onClick={openModal}
              className="flex items-center gap-2 whitespace-nowrap bg-white/5 border border-white/10 text-foreground-400 text-sm font-bold px-4 py-2.5 rounded-full transition-colors duration-200 cursor-pointer w-fit"
            >
              <i className="ri-user-line w-5 h-5 flex items-center justify-center"></i>
              Acceso Admin
            </button>
          )}
        </div>
      )}
    </header>
  );
}